import {
  cellTexts, OverviewCell, OverviewCellKind, OverviewIntensityTag, OverviewMark, OverviewRow, OverviewSessionTag, TrainingOverviewModel,
} from 'services/training-overview';
import { Formatting } from 'util/formatting';

// A hand-written Training Overview for the Test UI Components page (#5456): BORI's Workout A from
// the walkthrough in design-docs/coaching-dashboard.md. It holds every kind of cell, both marks,
// every session tag, a heavy/light row, a grey row and an "Other sessions" table, so each can
// be looked at without finding an account whose history has it.

const EMPTY: OverviewCell = {
  kind: OverviewCellKind.Empty, setsText: '', weightText: '', repsText: '',
  intensityTag: OverviewIntensityTag.None, sessionTag: OverviewSessionTag.None, mark: OverviewMark.None,
};
const SKIPPED: OverviewCell = { ...EMPTY, kind: OverviewCellKind.Skipped };

/** a trained cell whose sets all used one weight */
function trained(weightText: string, repsText: string, extra: Partial<OverviewCell> = {}): OverviewCell {
  return { ...EMPTY, kind: OverviewCellKind.Trained, setsText: `${weightText} × ${repsText}`, weightText, repsText, ...extra };
}

/** a trained cell whose sets used different weights: only the whole text is known */
function mixed(setsText: string, extra: Partial<OverviewCell> = {}): OverviewCell {
  return { ...EMPTY, kind: OverviewCellKind.Trained, setsText, ...extra };
}

/**
 * a trained cell written exactly as `formatExerciseSets` writes it: a no-break space between number
 * and unit and after each comma, the times sign, and "; " between the sets of one weight and the next
 */
function formatted(groups: [number, number[]][], extra: Partial<OverviewCell> = {}): OverviewCell {
  const groupTexts = groups.map(([kg, reps]) => `${kg}${Formatting.thinSpace}kg${Formatting.times}${reps.join(`,${Formatting.thinSpace}`)}`);
  const setsText = groupTexts.join(Formatting.setGroupSeparator);
  return { ...EMPTY, kind: OverviewCellKind.Trained, ...cellTexts(setsText, groups.length === 1, Formatting.times), ...extra };
}

const heavy = { intensityTag: OverviewIntensityTag.Heavy };
const light = { intensityTag: OverviewIntensityTag.Light };
const same = { mark: OverviewMark.Same };
const down = { mark: OverviewMark.Down };

function programRow(exerciseId: string, name: string, setsText: string, repTargetText: string, cells: OverviewCell[]): OverviewRow {
  return { exerciseId, name, isInProgram: true, isExerciseMissing: false, subSlotId: `${exerciseId}-slot`, setsText, repTargetText, cells };
}

function greyRow(exerciseId: string, name: string, cells: OverviewCell[], isExerciseMissing = false): OverviewRow {
  return { exerciseId, name, isInProgram: false, isExerciseMissing, cells };
}

const DATES_A = ['2026-08-24', '2026-08-31', '2026-09-07', '2026-09-14', '2026-09-21', '2026-09-28', '2026-10-05'];

export const SAMPLE_TRAINING_OVERVIEW: TrainingOverviewModel = {
  latestSameCount: 1,
  latestDownCount: 0,
  sections: [
    {
      planReference: 0,
      title: 'Workout A',
      badge: 'A',
      exerciseNamesText: 'Back Squat, Bench Press, Romanian Deadlift, Hip Thrust, Leg Extension',
      sessionCount: 23,
      columns: DATES_A.map(dateIso => ({ workoutId: `log/${dateIso}`, dateIso })),
      programRows: [
        programRow('squat', 'Back Squat', '3', '8', [
          EMPTY, EMPTY,
          trained('120 kg', '3', { sessionTag: OverviewSessionTag.Test }),
          trained('105 kg', '8, 7, 6'),
          trained('100 kg', '8, 8, 7', { sessionTag: OverviewSessionTag.Break }),
          trained('110 kg', '7, 6, 5'),
          trained('110 kg', '8, 7, 6'),
        ]),
        programRow('bench', 'Bench Press', '3', '6/10', [
          trained('67.5 kg', '10, 9, 8', light),
          trained('82.5 kg', '5, 4, 4', heavy),
          trained('70 kg', '8, 8, 7', light),
          trained('82.5 kg', '5, 4, 4', { ...heavy, ...same }),
          trained('70 kg', '9, 8, 7', light),
          SKIPPED,
          trained('70 kg', '9, 8, 8', { ...light, ...same }),
        ]),
        programRow('rdl', 'Romanian Deadlift', '3', '10', [
          trained('85 kg', '8, 7, 6'),
          trained('85 kg', '9, 8, 7'),
          trained('85 kg', '10, 8, 8', { sessionTag: OverviewSessionTag.Cap }),
          trained('90 kg', '8, 7, 7'),
          mixed('90 kg × 7, 7; 85 kg × 8', down),
          trained('85 kg', '10, 9, 8', { sessionTag: OverviewSessionTag.Deload }),
          trained('90 kg', '10, 9, 8'),
        ]),
        // #6691: long weights and 4 sets, written with the no-break spaces the real formatter uses, so
        // a phone row must wrap after a comma and never run past its card
        programRow('hip-thrust', 'Hip Thrust', '4', '20', [
          formatted([[120, [20, 18, 18, 16]]]),
          formatted([[122, [20, 18, 18, 16]]]),
          EMPTY,
          formatted([[112.5, [22, 20, 20, 18]]], down),
          formatted([[122.5, [20, 18]], [117.5, [18, 16]]], heavy),
          formatted([[1122.5, [22, 20, 20, 18, 16]]], { sessionTag: OverviewSessionTag.Deload }),
          formatted([[125, [20, 18, 18, 16]]]),
        ]),
        programRow('leg-extension', 'Leg Extension', '2', '~15', [
          trained('42.5 kg', '13+4+3+3'),
          trained('42.5 kg', '13+4+4+3', same),
          trained('42.5 kg', '14+4+4+3'),
          trained('42.5 kg', '15+5+4+3'),
          trained('45 kg', '13+4+3+3'),
          trained('45 kg', '14+4+3+3'),
          trained('45 kg', '15+4+4+3'),
        ]),
      ],
      greyRows: [
        greyRow('leg-press', 'Leg Press', [
          trained('150 kg', '10, 9, 8'),
          trained('150 kg', '10, 9, 8', same),
          EMPTY, EMPTY, EMPTY, EMPTY, EMPTY,
        ]),
      ],
    },
    {
      planReference: 1,
      title: 'Workout B',
      badge: 'B',
      exerciseNamesText: 'Deadlift, Overhead Press',
      sessionCount: 0,
      columns: [],
      programRows: [
        programRow('deadlift', 'Deadlift', '3', '5', []),
        programRow('ohp', 'Overhead Press', '3', '–', []),
      ],
      greyRows: [],
    },
    {
      planReference: undefined,
      title: 'Other sessions',
      badge: '',
      exerciseNamesText: '',
      sessionCount: 2,
      columns: [{ workoutId: 'log/2026-08-05', dateIso: '2026-08-05' }, { workoutId: 'log/2026-08-12', dateIso: '2026-08-12' }],
      programRows: [],
      greyRows: [
        greyRow('pull-up', 'Pull-up', [trained('10 kg', '6, 5, 5'), trained('10 kg', '6, 6, 5', same)]),
        greyRow('deleted', 'Deleted exercise', [trained('20 kg', '12, 12'), EMPTY], true),
      ],
    },
  ],
};
